import React from 'react';

import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter6.1.jpg';
import chapterImg1 from '../../../../assets/images/courses/teamplayer&collaboration/chapter6.2.jpg';
import chapterImg2 from '../../../../assets/images/courses/teamplayer&collaboration/chapter6.3.jpg';
import chapterImg3 from '../../../../assets/images/courses/teamplayer&collaboration/chapter6.4.jpg';
import chapterImg4 from '../../../../assets/images/courses/teamplayer&collaboration/chapter6.5.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import FlipCard from '../../FlipCard';
import { InfoBlock } from '../../InfoBlock';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter6: React.FC<TeamPlayersAndCollaborationChapter1> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Becoming a Team Culture Creator</h2>
        <br />
        <MediaBlock mediaImage={introImg}></MediaBlock>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Creating a strong team culture is essential for fostering collaboration, motivation, and
          success within any group. Becoming a team culture creator means actively contributing to
          an environment where teamwork thrives, and individuals feel valued and supported.
        </p>
        <br />
        <p className="text-lg text-left font-semibold mt-10 mb-10">
          This section explores powerful practices to enhance your team's collaboration, the
          importance of "paying it forward" through mutual support, and additional tips to build a
          positive team culture. By focusing on these elements, you can help your team achieve more
          together than any individual could alone.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-1">
          Capitalize on Collaboration
        </h3>
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Collaboration is at the heart of teamwork and becoming a team culture creator. When done
          right, the team adds up to much more than the sum of its parts. Here are three powerful
          practices to help you enhance your team's collaboration.
        </p>
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            1
          </div>
        </div>
        <br />
        <h3 className="text-2xl  font-bold mt-10 mb-5">Continually Clarify Context</h3>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1 leading-7">
            Changes are constant and it's vital to continually clarify the team's purpose in the
            context of the big picture. Without it, the team fragments. What's the teams purpose?
            How do team members contribute? And finally, if applicable, how does this project
            contribute?
          </p>
        </div>
        <br />
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            2
          </div>
        </div>
        <br />
        <h3 className="text-2xl  font-bold mt-10 mb-5">The Art of Consultation</h3>
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-7">
            Imagine if your team could hit that sweet spot where everyone feels safe to share
            different ideas, and those ideas help the whole group grow stronger together. It’s about
            making sure everyone’s opinion is valued and knowing that the best decisions come from
            using everyone’s knowledge. To make this happen, everyone needs to be open and honest.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
        </div>
        <br />
        <p className="text-lg text-center font-semibold mt-10 mb-10">
          Here are three key principles to help your team work better together through consultation:
        </p>
        <p className="text-lg text-left font-bold mt-10 mb-10">
          Click each card to learn these three principles.
        </p>
        <div className="my-10">
          <div className="flex justify-center gap-5 mb-7">
            <FlipCard>
              <div className="front-content text-center">
                <p>Encourage Open Sharing</p>
              </div>
              <div className="back-content text-center  w-full h-full flex items-center justify-center">
                <div>
                  <p>
                    Make sure everyone feels comfortable sharing their ideas, even if they’re
                    different from others.
                  </p>
                </div>
              </div>
            </FlipCard>
            <FlipCard>
              <div className="front-content text-center">
                <p>Value Every Option</p>
              </div>
              <div className="back-content text-center  w-full h-full flex items-center justify-center">
                <div>
                  <p>Treat all ideas as important and consider them carefully.</p>
                </div>
              </div>
            </FlipCard>
            <FlipCard>
              <div className="front-content text-center">
                <p>Build on Ideas Together</p>
              </div>
              <div className="back-content text-center  w-full h-full flex items-center justify-center">
                <div>
                  <p>Work together to combine different ideas into the best possible solution.</p>
                </div>
              </div>
            </FlipCard>
          </div>
        </div>
        <p className="text-lg text-center font-semibold mt-10 mb-10">
          By following these principles, teams can make better decisions and stay united.
          Consultation takes practice and the payoff can be immeasurable. Through the trust built,
          people are empowered to solve problems that require deep collaboration.
        </p>
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            3
          </div>
        </div>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          In collaborative teams, there's an unspoken rule that everyone supports each other. This
          idea, known as <strong>"paying it forward</strong>," is called generalized reciprocity.
          Let's learn more.
        </p>
        <p className="text-lg text-center font-semibold mt-10 mb-10">
          <strong> Paying it forward</strong> means that team members trust each other and feel a
          mutual responsibility to help one another. This trust and sense of duty often come from
          working together over time and feeling like a close-knit group.
        </p>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg3} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <strong>Example: School Sports Team</strong>
            <br />
            Imagine you’re on a school sports team. One of your teammates is struggling with their
            part in a play. Instead of just focusing on your own role, you take time to help them
            improve. Later, when you’re having a tough game, that same teammate steps up to support
            you. This mutual support helps the team perform better and creates a positive team
            spirit where everyone looks out for each other.
          </p>
        </div>
        <br />
        <InfoBlock
          title="Note: "
          text="It's important to encourage others to contribute their strengths to the team too. The best teams know each other's strengths, how to get the best out of one another, and how to energize each other so that everyone performs harmoniously. This is how you can become a team culture creator."
        />
        <br />
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-7">
            <br />
            Building a positive team culture requires intentional effort and consistent practice. By
            capitalizing on collaboration, fostering open consultation, and encouraging mutual
            support through "paying it forward," you can significantly enhance your team's cohesion
            and effectiveness. Additional practices such as respect, clear communication, support,
            and maintaining a positive attitude further contribute to a thriving team environment.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg4} alt="" />
          </div>
        </div>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Reflect</h3>
        <div id="print-view" className=" p-4 rounded-lg">
          <p className="text-lg font-normal flex-1 mt-5 mb-5">
            Think about how you can "pay it forward" in your own teams. It could be as simple as
            helping a classmate with homework or supporting a teammate during practice. Write your
            responses below.
          </p>
          <textarea
            className="w-full h-32 p-2 border rounded"
            placeholder="Enter your text here..."
          ></textarea>

          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />{' '}
        <h3 className="text-2xl mt-2 text-center font-light mb-5">
          Supporting your team members is a crucial part of being a great team player. Click
          continue to learn some ways to be nice on a team.
        </h3>
        <br />
        <h3 className="text-2xl font-bold mt-10 m-5">Ways to be Nice on a Team</h3>
        <p className="text-lg text-left font-normal mt-5 mb-10">
          While some may find it easier than others to be nice on a team, it's important for
          everyone to make an effort. Instead of leaving it to someone else, we should all pitch in
          to support each other. So, what makes someone the nicest person ever in a team?
        </p>
        <div className="flex flex-col items-center relative min-h-screen py-10">
          {/* Vertical Line (Stops before the last div) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 h-[calc(100%-100px)]"></div>

          {/* Cards */}
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Listen actively:</h3>
            <p className="text-xl font-light mb-8">
              Great team players take the time to listen to their colleagues without judgment. They
              offer a supportive ear and show genuine interest in what others have to say.
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Show empathy:</h3>
            <p className="text-xl font-light mb-8">
              They understand and acknowledge the feelings and perspectives of their colleagues.
              They offer empathy and support, especially during challenging times.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Offer assistance:</h3>
            <p className="text-xl font-light mb-8">
              Whether it's lending a helping hand with a task, offering guidance, or providing
              emotional support, they're always ready to assist their colleagues in any way they
              can.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Celebrate successes:
            </h3>
            <p className="text-xl font-light mb-8">
              They genuinely celebrate the achievements and milestones of their colleagues. Whether
              it's a big win or a small accomplishment, they recognize and cheer for their
              teammates' success.
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Provide constructive feedback:
            </h3>
            <p className="text-xl font-light mb-8">
              Great team players offer feedback in a constructive and respectful manner. They focus
              on helping their colleagues improve and grow rather than criticizing or tearing them
              down.
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Promote collaboration:
            </h3>
            <p className="text-xl font-light mb-8">
              They actively foster a collaborative environment where everyone's ideas are valued and
              encouraged. They work together with their team members to achieve common goals and
              objectives.
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">Express gratitude:</h3>
            <p className="text-xl font-light mb-8">
              They show appreciation for their team members’ contributions and efforts. Whether it's
              a simple thank you or a thoughtful gesture, they make sure their teammates feel valued
              and respected.
            </p>
          </div>
        </div>
        <br />
        <p className="text-2xl text-center font-normal mt-5 mb-10">
          As you apply these principles, you'll not only improve your team's performance but also
          develop valuable skills that will benefit you in any collaborative endeavor. By becoming a
          team culture creator, you play a pivotal role in creating an environment where everyone
          feels valued and motivated to do their best.
        </p>
        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Hone Your Strategy Skills
        </button>
      </div>
    </>
  );
};
